<script>
  import { format } from 'd3-format';
  import { scaleThreshold } from 'd3-scale';
  import { group, rollup, sum } from 'd3-array';
  import CountiesMap from '../countiesMap/CountiesMap.svelte';
  import { apnumber, intcomma, pluralize } from 'journalize';

  // Components
  import Legend from '../Legend.svelte';
  import Toggle from '../Toggle.svelte';  
  import Tooltip from '../Tooltip.svelte';
  import { clamp } from '../../utils/clamp';

  // Translation
  import { __ } from '../../utils/i18n';

  export let data;
  export let tiers;

  // Set the default values
  let attribute = 'all';

  // Figure out what field to use
  const fieldCrosswalk = {
    all: 'total_in_school',
    elem: 'elem_in_school',
    secondary: 'secondary_in_school',
  }; 
  $: getField = () => {
    return fieldCrosswalk[attribute];
  };

  const enrCrosswalk = {
    all: 'enr_total',
    elem: 'elem_enr',
    secondary: 'secondary_enr',
  }; 
  $: getEnr = () => {
    return enrCrosswalk[attribute];
  };

  const attributeButtons = [
    { label: __('All'), value: 'all' },
    { label: __('Elementary'), value: 'elem' },
    { label: __('Secondary'), value: 'secondary' },
  ];

  const handleAttributeClick = (e) => {
    attribute = e.target.getAttribute('data-value');
  };

  $: mapData = data.map((d) => ({
    fips: d.fips,
    value: d[getEnr()]>0? d[getField()]/d[getEnr()]:null,
  }));

  let breaks = [0.2, 0.4, 0.6, 0.8, 1];

  //blue
  let scheme = [
    '#BEE9F4',
    '#8FD0E1',
    '#60B7D3',
    '#3995B7',
    '#026F90',
    '#00435c',
  ];

  let thresholds = scaleThreshold().domain(breaks).range(scheme);

  let color = (v) => (v != undefined ? thresholds(v) : '#dddddd');

  let percentFormatter = format('.0%');

  let legendFormatter = (i, scale) => percentFormatter(scale[i]);

  let tooltipSlug = 'tooltip__school-map';
  let tooltipHeader = '';
  let tooltipSubhed = '';
  let tooltipValues = [];
  let showTooltip = false;
  let tooltipClass = '';

  let tooltipLookup = data.reduce((obj, item) => {
    obj[item.fips] = item;
    return obj;
  }, {});

  // Iterate through tooltip lookup, iterate through tiers and match on fips

  Object.keys(tooltipLookup).forEach(function(key) {
    tiers.forEach(d=>{
      if (key == d.fips){
      tooltipLookup[key].tier = d.tier 
      tooltipLookup[key].tier_label = d.tier_label
      }
    })  
  })

  function handleMapActivateCounty(e, push = 20) {
    showTooltip = true;

    const fips = e.detail.fips;
    const tooltipData = tooltipLookup[fips] || {};
    tooltipHeader = e.detail.name;
    tooltipSubhed = `Tier ${tooltipData.tier}`;
    tooltipClass = `${tooltipData.tier}`

    let value;

    if (e.detail.value == null) {
      value = [`No data.`]
    } else if (attribute == 'all') {
      value = [
        `${(
            tooltipData.total_in_school/tooltipData.enr_total * 100
          ).toFixed(0)}% of students have returned to in-person or hybrid learning.`,
      ];
    } else if (attribute == 'elem') {
      value = [
        `${(
            tooltipData.elem_in_school/tooltipData.elem_enr * 100
          ).toFixed(0)}% of elementary school students have returned to in-person or hybrid learning.`,
      ];
    } else if (attribute == 'secondary') {
      value = [
        `${(
            tooltipData.secondary_in_school/tooltipData.middle_enr * 100
          ).toFixed(0)}% of secondary school students have returned to in-person or hybrid learning.`,
      ];
    }

    tooltipValues = [value];

    const el = document.getElementById(tooltipSlug);
    const { width, height } = el.getBoundingClientRect();
    const innerWidth = window.innerWidth;
    const innerHeight = window.innerHeight;
    const x = event.clientX;
    const y = event.clientY;
    const left = clamp(x - width / 2, push, innerWidth - width - push);
    const top =
      innerHeight > y + push * 2 + height ? y + push : y - height - push;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  function handleMapDeactivateCounty(e) {
    showTooltip = false;
  }
</script>

<div class="svg-container svg-container__california-counties">
  <div class="button-grid california-counties-controls">
    <div class="button-grid-group">
      <Toggle
        label={__('Grade level')}
        slug="school-type"
        buttonList={attributeButtons}
        selectedValue={attribute}
        on:click={handleAttributeClick}
      />
    </div>
  </div>  
  <div class="legend">
    <Legend
      height="60"
      color={thresholds}
      title="Percentage of students back in school"
      tickFormatFunc={legendFormatter}
    />
  </div>
  <CountiesMap
    bind:data={mapData}
    colorScale={color}
    on:mapActivateCounty={handleMapActivateCounty}
    on:mapDeactivateCounty={handleMapDeactivateCounty}
  />
<div
  id={tooltipSlug}
  class="tooltip"
  class:invisible={!showTooltip}
  data-show-tooltip={showTooltip}
>
  <div>
      <p class="tooltip__header">{tooltipHeader} <span class="tier_label county_tier_{tooltipClass}">{tooltipSubhed}</span></p>
    {#each tooltipValues as obj}
      <p class="tooltip__value">
        {@html obj}
      </p>
    {/each}
  </div>
</div>  
</div>
